import React, { useState, useEffect } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { successAlert, errorAlert } from "../../utils/alert";
import { FaUserShield, FaSpinner, FaSave, FaInfoCircle } from "react-icons/fa";

const OtpSettings = () => {
  const [settings, setSettings] = useState({
    enable_registration_otp: true,
    enable_password_reset_otp: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await API.fetchOtpSettings();
      setSettings({
        enable_registration_otp: !!(res?.enable_registration_otp ?? true),
        enable_password_reset_otp: !!(res?.enable_password_reset_otp ?? true),
      });
    } catch (err) {
      console.error(err);
      errorAlert("Failed to load settings", err?.message || "Could not fetch OTP configurations.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (field) => {
    setSettings((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.updateOtpSettings(settings);
      successAlert("Settings Saved", "OTP security settings have been updated successfully.");
    } catch (err) {
      console.error(err);
      errorAlert("Save Failed", err?.message || "An error occurred while saving configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="relative min-h-screen bg-slate-100 mobile-page-pad overflow-hidden">
        {/* Glow Effects */}
        <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-indigo-300/35 blur-3xl hidden sm:block" />
        <div className="pointer-events-none absolute top-40 -left-20 h-72 w-72 rounded-full bg-sky-300/30 blur-3xl hidden sm:block" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.08)_1px,transparent_1px)] bg-[size:28px_28px] hidden sm:block" />

        <div className="relative max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3.5 mb-8">
            <div className="p-3 bg-indigo-150 text-indigo-750 rounded-2xl shadow-sm border border-indigo-200 bg-indigo-50">
              <FaUserShield size={28} className="text-indigo-650" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">OTP Security Settings</h1>
              <p className="text-sm text-slate-500 mt-1">
                Configure when and where one-time passcode verification is enforced for public and administrative workflows.
              </p>
            </div>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div className="bg-white/90 backdrop-blur rounded-3xl border border-slate-200 shadow-md p-6 sm:p-8 space-y-6">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-800">OTP Verification Switches</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Toggle each setting below to bypass or enforce OTP validation.</p>
                </div>
                <div className="hidden sm:block">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    <FaInfoCircle size={10} /> System configurations
                  </span>
                </div>
              </div>

              {loading ? (
                <div className="py-16 flex flex-col items-center justify-center text-slate-400 gap-3">
                  <FaSpinner className="animate-spin text-indigo-600" size={32} />
                  <p className="text-sm font-medium">Fetching secure configurations...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Switch 1: Registration OTP */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-slate-150 hover:border-indigo-200 hover:bg-slate-50/50 transition-all">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-800 text-sm sm:text-base">Player Registration OTP</h3>
                        {settings.enable_registration_otp ? (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-250">
                            Enforced
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-250">
                            Bypassed
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-xl">
                        Requires players to verify their email address via a 6-digit OTP code before completing the public signup form.
                        If disabled, any signup application will submit immediately without email validation.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => handleToggle("enable_registration_otp")}
                        className={`relative inline-flex h-6.5 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          settings.enable_registration_otp ? "bg-indigo-600" : "bg-slate-300"
                        }`}
                        style={{ height: "26px", width: "48px" }}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            settings.enable_registration_otp ? "translate-x-5.5" : "translate-x-0.5"
                          }`}
                          style={{
                            height: "20px",
                            width: "20px",
                            transform: settings.enable_registration_otp ? "translateX(22px)" : "translateX(2px)",
                            marginTop: "1px",
                          }}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Switch 2: Password Reset OTP */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-slate-150 hover:border-indigo-200 hover:bg-slate-50/50 transition-all">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-800 text-sm sm:text-base">Profile Password Change OTP</h3>
                        {settings.enable_password_reset_otp ? (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-250">
                            Enforced
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-250">
                            Bypassed
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-xl">
                        Controls OTP when signed-in users modify their password from the profile panel. Public Forgot Password recovery
                        always requires email OTP and cannot be disabled, preventing account recovery from being blocked or bypassed.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => handleToggle("enable_password_reset_otp")}
                        className={`relative inline-flex h-6.5 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          settings.enable_password_reset_otp ? "bg-indigo-600" : "bg-slate-300"
                        }`}
                        style={{ height: "26px", width: "48px" }}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            settings.enable_password_reset_otp ? "translate-x-5.5" : "translate-x-0.5"
                          }`}
                          style={{
                            height: "20px",
                            width: "20px",
                            transform: settings.enable_password_reset_otp ? "translateX(22px)" : "translateX(2px)",
                            marginTop: "1px",
                          }}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between border-t border-slate-100 pt-5">
                <div className="text-[11px] sm:text-xs text-slate-400 flex items-center gap-1.5">
                  <FaInfoCircle /> Changes take effect immediately across all platforms.
                </div>
                <button
                  type="submit"
                  disabled={loading || saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-xs sm:text-sm font-bold text-white transition-all hover:bg-indigo-700 shadow-md hover:shadow-indigo-150 disabled:bg-slate-350 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <FaSpinner className="animate-spin" size={13} /> Saving Configuration...
                    </>
                  ) : (
                    <>
                      <FaSave size={13} /> Update OTP Settings
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default OtpSettings;
